import axios from 'axios';

// ----------------------------------------------------------------------

const axiosInstance = axios.create();
const interceptorsRegisteredTargets = new WeakSet();

const now = () =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();

const getNewRelic = () => (typeof window !== 'undefined' ? window.newrelic : undefined);

const safeAddPageAction = (actionName, payload) => {
  const nr = getNewRelic();
  if (!nr || typeof nr.addPageAction !== 'function') return;
  try {
    nr.addPageAction(actionName, payload);
  } catch (error) {
    // Intentionally swallow errors from monitoring SDK calls.
  }
};

export const registerAxiosNewRelicInterceptors = () => {
  return registerGlobalAxiosNewRelicInterceptors();
};

export const attachNewRelicInterceptors = (
  targetAxios,
  { preserveLegacyErrorShape = false } = {},
) => {
  if (!targetAxios?.interceptors) return targetAxios;
  if (interceptorsRegisteredTargets.has(targetAxios)) return targetAxios;

  targetAxios.interceptors.request.use(
    (config) => {
      config.metadata = config.metadata || {};
      config.metadata.startTime = now();
      return config;
    },
    (error) => Promise.reject(error),
  );

  targetAxios.interceptors.response.use(
    (response) => {
      const startTime = response?.config?.metadata?.startTime;
      const duration = typeof startTime === 'number' ? now() - startTime : null;
      safeAddPageAction('api_call', {
        url: response?.config?.url || '',
        method: (response?.config?.method || 'get').toUpperCase(),
        duration,
        statusCode: response?.status ?? null,
        success: true,
      });
      return response;
    },
    (error) => {
      const config = error?.config || {};
      const startTime = config?.metadata?.startTime;
      const duration = typeof startTime === 'number' ? now() - startTime : null;
      const statusCode = error?.response?.status ?? null;
      const responseMessage =
        error?.response?.data?.message || error?.message || 'Something went wrong';
      safeAddPageAction('api_call', {
        url: config?.url || '',
        method: (config?.method || 'get').toUpperCase(),
        duration,
        statusCode,
        success: false,
        errorMessage: responseMessage,
      });

      const nr = getNewRelic();
      if (nr && typeof nr.noticeError === 'function') {
        try {
          nr.noticeError(error, {
            url: config?.url || '',
            method: (config?.method || 'get').toUpperCase(),
            statusCode,
            responseMessage,
          });
        } catch (noticeErrorCallError) {
          // Intentionally swallow errors from monitoring SDK calls.
        }
      }

      if (preserveLegacyErrorShape) {
        return Promise.reject(
          (error.response && error.response.data) || 'Something went wrong',
        );
      }
      return Promise.reject(error);
    },
  );

  interceptorsRegisteredTargets.add(targetAxios);
  return targetAxios;
};

export const registerGlobalAxiosNewRelicInterceptors = () =>
  attachNewRelicInterceptors(axios);

attachNewRelicInterceptors(axiosInstance, { preserveLegacyErrorShape: true });

export default axiosInstance;
